import { Box, Grid, Stack } from '@mui/material';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import {
  ClosureDTO,
  ICandidate,
  IJob,
  IJobSearchFilterDTO,
  getJobById,
  indexJob,
} from 'wisestep-api';
import { ControlledTextArea, ControlledTextInput, WsButton, WsLocationSearch } from 'wisestep-base';

import { failed, success } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { jobSearchPayload } from '../helper/payload';

import ClosureJobPopup from './ClosureJobPopup';

export interface IComponentProps {
  formWizardSubMenuItemClick: (index: number) => void;
  setClosureDTO: (data: ClosureDTO) => void;
  currentClosureDTO: ClosureDTO;
  onCancel: () => void;
  setDirtyFieldsValue: (data: any) => void;
  getDirtyFieldsValue: any;
}

const ClosureJobDetails = ({
  formWizardSubMenuItemClick,
  setClosureDTO,
  currentClosureDTO,
  setDirtyFieldsValue,
  getDirtyFieldsValue,
  onCancel,
}: IComponentProps) => {
  const location: any = useLocation();
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [openJobPopup, setOpenJobPopup] = useState<boolean>(false);
  const [selectedJob, setSelectedJob] = useState<IJob>();
  const methods = useForm<IJob>();
  const {
    formState: { dirtyFields },
  } = methods;

  const jobSearchDTO: IJobSearchFilterDTO = {
    ...jobSearchPayload,
    clientName: currentClosureDTO.clientName,
  };

  const onSubmit = async (jobEntity: IJob) => {
    setDirtyFieldsValue({ ...getDirtyFieldsValue, ...dirtyFields });

    if (selectedJob?.id) {
      setClosureDTO({ ...currentClosureDTO, positionId: selectedJob?.id ?? '' });
      formWizardSubMenuItemClick(2);
    } else if (location?.state?.candidate) {
      const candidateFromSubmission: ICandidate =
        location.state && location.state.candidate ? location.state.candidate : undefined;
      setClosureDTO({ ...currentClosureDTO, positionId: candidateFromSubmission.jobId ?? '' });
      formWizardSubMenuItemClick(2);
    } else {
      try {
        jobEntity.clientId = currentClosureDTO.clientId;
        jobEntity.companyName = currentClosureDTO.clientName;
        const response = await indexJob(jobEntity);
        if (response) {
          setClosureDTO({ ...currentClosureDTO, positionId: response.data?.id });
          setNotification(success(`Job details successfully saved.`));
          formWizardSubMenuItemClick(2);
        }
      } catch (error) {
        setNotification(failed(`Something went wrong while saving job details.`));
      }
    }
  };

  const setSelectedJobDetail = (job: IJob) => {
    setSelectedJob(job);
    setClosureDTO({
      ...currentClosureDTO,
      positionId: job?.id ?? '',
      positionTitle: job?.title ?? '',
      clientContactId: job?.clientContactId,
    });
    methods.setValue('title', job.title);
    methods.setValue('locations', job.locations);
    methods.setValue('description', job.description);
  };

  useEffect(() => {
    const getJobDetails = async () => {
      if (currentClosureDTO.positionId) {
        const jobResponse = await getJobById(currentClosureDTO.positionId);
        if (jobResponse?.data) {
          const jobEntity: IJob = jobResponse.data;
          methods.setValue('title', jobEntity.title ?? '');
          methods.setValue('locations', jobEntity.locations ?? []);
          methods.setValue('description', jobEntity.description ?? '');
          setSelectedJob(jobEntity);
        }
      } else if (location?.state?.candidate) {
        const candidateFromSubmission: ICandidate =
          location.state && location.state.candidate ? location.state.candidate : undefined;
        if (candidateFromSubmission.jobId) {
          const jobResponse = await getJobById(candidateFromSubmission.jobId as string);
          if (jobResponse?.data) {
            const jobEntity: IJob = jobResponse.data;
            methods.setValue('title', jobEntity.title ?? '');
            methods.setValue('locations', jobEntity.locations ?? []);
            methods.setValue('description', jobEntity.description ?? '');
            setSelectedJob(jobEntity);
          }
        } else {
          methods.setValue('title', candidateFromSubmission.mappedJobTitle ?? '');
          methods.setValue('locations', candidateFromSubmission.jobLocations ?? []);
        }
      }
    };
    getJobDetails();
  }, [currentClosureDTO, location, methods]);

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <Box borderRadius={1.5}>
            <Grid container py={1}>
              <Grid item p={1} sm={11.5}>
                <WsButton
                  variant="outlined"
                  size="large"
                  type="button"
                  onClick={() => setOpenJobPopup(true)}
                >
                  {`Select existing Job`}
                </WsButton>
              </Grid>
              <Grid item p={1} sm={5.75}>
                <ControlledTextInput
                  name="title"
                  type="text"
                  fullWidth
                  labelText="Job Title"
                  variant="outlined"
                  showMandatorySymbol
                  defaultValue={selectedJob?.title}
                  disabled={selectedJob?.id ? true : false}
                />
              </Grid>
              <Grid item p={1} sm={5.75}>
                <WsLocationSearch
                  size="small"
                  placeHolder="Job Location"
                  name="locations"
                  variant="outlined"
                  showMandatorySymbol={true}
                  disabled={selectedJob?.id ? true : false}
                />
              </Grid>
              <Grid item p={1} sm={11.5}>
                <ControlledTextArea
                  name="description"
                  defaultValue={selectedJob?.description}
                  labelText="Job Description*"
                  minRows={8}
                />
              </Grid>
            </Grid>
          </Box>
          <Stack
            p={1}
            spacing={2}
            direction="row"
            sx={{ display: 'flex', justifyContent: 'right' }}
          >
            <WsButton variant="outlined" size="medium" type="button" onClick={onCancel}>
              {`Cancel`}
            </WsButton>
            <WsButton variant="contained" size="medium" type="submit">
              {selectedJob?.id || location?.state?.candidate ? 'Next' : `Create Job`}
            </WsButton>
          </Stack>
        </form>
      </FormProvider>
      {openJobPopup && (
        <ClosureJobPopup
          setOpenJobPopup={setOpenJobPopup}
          setSelectedJob={setSelectedJobDetail}
          jobSearchDTO={jobSearchDTO}
        />
      )}
    </>
  );
};

export default ClosureJobDetails;
